/**
 * Provador Virtual — Versiani Swim.
 *
 * Réplica do provador que a loja usa hoje (Sizebay), rodando dentro do tema.
 * A cliente informa altura, peso e idade, ajusta o formato do corpo e recebe o
 * tamanho recomendado com o caimento medida a medida.
 *
 * Por que replicar em vez de manter a Sizebay:
 *   - o dado que decide o tamanho é a TABELA DA VS, que já é nossa;
 *   - a Sizebay injeta um botão por seletor de tamanho — no conjunto vira dois
 *     provadores soltos na página. Aqui é um botão só, com as peças em abas;
 *   - sem iframe de terceiro na PDP: nada de script externo no caminho crítico.
 *
 * ═══ O QUE FOI COPIADO E DE ONDE ═══
 *
 * O fluxo, os textos e a medida de cada elemento saíram do provador REAL da
 * Sizebay, percorrido no navegador em 2026-08-15 (CDP, com o iframe deles
 * atachado): 4 telas (dados → formato → cálculo → resultado), a tela "Minhas
 * Medidas", o painel "Medidas corporais", a tabela de medidas e a recomendação
 * escrita na PDP embaixo do seletor de tamanho.
 *
 * ⚠️ O QUE É ESTIMATIVA E O QUE É DADO REAL:
 *   - REAL: as medidas por tamanho (`provador-tabelas.liquid`), que são a
 *     modelagem que a loja usa hoje em produção.
 *   - CALIBRADO: busto/cintura/quadril deduzidos de altura+peso+idade. A forma
 *     da conta é escala física; as constantes foram ancoradas na saída do
 *     próprio provador atual (165 cm/60 kg/30 a → 91-75-97, lido na tela
 *     "Minhas Medidas" deles). É por isso que a recomendação bate com a de
 *     hoje em vez de mandar a cliente um tamanho abaixo.
 *   - EDITÁVEL: a cliente sobrescreve tudo em "Editar Medidas", e aí não sobra
 *     estimativa nenhuma — vale a fita métrica dela.
 *
 * Vanilla de propósito, mesmo motivo do tryon.js: a PDP já leva Clarity,
 * ContentSquare, VWO e o container do GTM.
 */
(function () {
  'use strict';

  /** Perfil da cliente. Fica no aparelho dela — nada disso vai pro servidor. */
  var CHAVE = 'vs-provador-v1';

  /** Cada passo do slider de formato mexe 2 cm na medida. */
  var PASSO_CM = 2;
  var PASSO_MAX = 2;

  /** Um tom só, o marfim de manequim — a pedido: a boneca é referência de
   *  FORMA, e a paleta de pele só dava escolha sem consequência nenhuma. */
  var MARFIM = '#f1efec';

  var EIXOS = { busto: 'Busto', cintura: 'Cintura', quadril: 'Quadril' };

  /* ------------------------------------------------------------- utilidades */

  /**
   * Trava a rolagem da PDP enquanto o popup está aberto.
   *
   * `showModal()` deixa o fundo inerte pra clique e foco, mas NÃO pra rolagem:
   * a roda do mouse e o arrasto no celular continuam correndo a página atrás do
   * provador. Mesma solução do try-on (`tryon.js`) — e `position: fixed` em vez
   * de só `overflow: hidden` porque no iOS o overflow sozinho não segura o
   * arrasto. O preço é que fixar o body joga a página pro topo, então guardamos
   * a posição e devolvemos exatamente onde estava.
   */
  var topoGuardado = null;

  function travarScroll() {
    if (topoGuardado !== null) return;
    topoGuardado = window.pageYOffset || document.documentElement.scrollTop || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -topoGuardado + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
  }

  function destravarScroll() {
    if (topoGuardado === null) return;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.style.overflow = '';
    window.scrollTo(0, topoGuardado);
    topoGuardado = null;
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function lerJson(txt) {
    try { return JSON.parse(txt || '{}') || {}; } catch (e) { return {}; }
  }

  /**
   * Rótulos da tabela de medidas. A tabela pode trazer medidas que não entram
   * na conta (o `sobBusto` do top) — na tabela elas aparecem, na recomendação não.
   */
  var ROTULOS = { busto: 'Busto', sobBusto: 'Sob o busto', cintura: 'Cintura', quadril: 'Quadril' };

  /**
   * Reinicia uma animação CSS no mesmo elemento. Tirar e pôr a classe no mesmo
   * quadro não reinicia nada — o `offsetWidth` força o navegador a ver o estado
   * sem a classe antes de devolvê-la.
   */
  function animar(n, cls) {
    if (!n) return;
    n.classList.remove(cls);
    void n.offsetWidth;
    n.classList.add(cls);
  }

  function num(v) {
    var n = parseFloat(String(v).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function lerPerfil() {
    try {
      var bruto = window.localStorage.getItem(CHAVE);
      if (!bruto) return null;
      var p = JSON.parse(bruto);
      if (!p || !p.altura || !p.peso) return null;
      p.ajustes = p.ajustes || {};
      return p;
    } catch (e) {
      return null;
    }
  }

  function salvarPerfil(p) {
    try {
      window.localStorage.setItem(CHAVE, JSON.stringify(p));
    } catch (e) {
      /* modo anônimo / storage cheio: o provador funciona, só não lembra. */
    }
  }

  function limparPerfil() {
    try { window.localStorage.removeItem(CHAVE); } catch (e) {}
  }

  /* ------------------------------------------------------ modelo do corpo */

  /**
   * Estima busto, cintura e quadril a partir de altura, peso e idade.
   *
   * A forma da conta é escala física, não regressão inventada: massa ≈
   * densidade × altura × área da seção do tronco, então a área cresce com
   * peso/altura e a circunferência — perímetro dessa seção — cresce com a RAIZ:
   *
   *     circunferência = k · √(peso / altura)
   *
   * As três constantes saem de uma âncora medida, não escolhida: 165 cm, 60 kg,
   * 30 anos → 91-75-97, que é exatamente o que o provador em produção devolve
   * para esse corpo (lido na tela "Minhas Medidas" dele em 2026-08-15). Com a
   * âncora anterior, chutada em 88-70-96, a recomendação saía um tamanho abaixo
   * da atual em dois produtos testados — diferença suficiente para virar troca.
   *
   * Idade entra só na cintura: é a única medida com efeito consistente e grande
   * o bastante para valer um termo. Aplicada em torno dos 30 e travada, para
   * não extrapolar onde não temos base.
   *
   * Recalibrar é trocar os três números abaixo — a conta não muda.
   */
  function estimarCorpo(altura, peso, idade) {
    var r = Math.sqrt(peso / altura);
    var corpo = {
      busto: 150.9 * r,
      cintura: 124.4 * r,
      quadril: 160.9 * r
    };
    var d = Math.max(-10, Math.min(30, (idade || 30) - 30));
    corpo.cintura += 0.12 * d;
    return corpo;
  }

  function comAjustes(corpo, ajustes) {
    var saida = {};
    Object.keys(corpo).forEach(function (k) {
      saida[k] = corpo[k] + (ajustes[k] || 0) * PASSO_CM;
    });
    return saida;
  }

  /**
   * Caimento a partir da folga (medida da peça − medida do corpo).
   *
   * As faixas reproduzem o vocabulário do provador atual, conferido em dois
   * produtos: −1 cm ele chama de "levemente justo", +1 e +5 de "levemente
   * folgado". Ou seja, a zona "levemente" é larga e o "perfeito" é estreito.
   */
  function rotuloFolga(folga) {
    if (folga <= -6) return { texto: 'muito justo', tom: 'apertado', icone: 'justo' };
    if (folga < -0.5) return { texto: 'levemente justo', tom: 'justo', icone: 'justo' };
    if (folga <= 0.5) return { texto: 'perfeito', tom: 'ideal', icone: 'ideal' };
    if (folga < 6) return { texto: 'levemente folgado', tom: 'folgado', icone: 'folgado' };
    return { texto: 'folgado', tom: 'folgado', icone: 'folgado' };
  }

  /** Penaliza o apertado mais que o folgado — em moda praia faltar é pior. */
  function penalidade(folga) {
    return folga < 0 ? -folga * 1.4 : folga;
  }

  /**
   * Folga "ampla" da tabela (a "variação personalizada" da Sizebay, ex.: Top Fita
   * +16 cm no busto — peça que amarra/estica). Com folga v o tamanho veste como se
   * medisse t + v/2: das regras testadas contra a Sizebay em 2026-10-15 foi a que
   * mais bateu (92% sem a Calcinha Fita; top do conjunto Fita 13/15, era 9/15).
   * Ver 03-Projects/Versiani-Try-Bay/comparacao-sizebay-2026-10-15.md.
   */
  function folgaDe(peca, eixo) {
    var f = (peca.folga || []).filter(function (x) { return x.eixo === eixo && x.tipo === 'ample'; })[0];
    return f ? Number(f.valor) || 0 : 0;
  }

  function avaliar(peca, corpo) {
    var lista = peca.tamanhos.map(function (t) {
      var eixos = peca.eixos
        .filter(function (e) { return typeof t[e] === 'number'; })
        .map(function (e) {
          var folga = t[e] + folgaDe(peca, e) / 2 - corpo[e];
          return { eixo: e, folga: folga, rotulo: rotuloFolga(folga) };
        });
      var soma = eixos.reduce(function (s, x) { return s + penalidade(x.folga); }, 0);
      return {
        nome: t.nome,
        medidas: t,
        eixos: eixos,
        penal: eixos.length ? soma / eixos.length : Infinity
      };
    });

    var melhor = lista.slice().sort(function (a, b) { return a.penal - b.penal; })[0];
    return { lista: lista, melhor: melhor };
  }

  /* ---------------------------------------------------------------- ícones */

  var ICONES = {
    justo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 8l4 4-4 4M10 8l-4 4 4 4"/></svg>',
    folgado: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 8l-4 4 4 4M15 8l4 4-4 4"/></svg>',
    ideal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'
  };

  /* ----------------------------------------------------------------- boneca */

  /** Clareia (dl > 0) ou escurece (dl < 0) uma cor hex, em pontos de luminosidade. */
  function tonalizar(hex, dl) {
    var n = parseInt(hex.slice(1), 16);
    var r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var l = (max + min) / 2, s = 0, h = 0;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    l = Math.max(0, Math.min(1, l + dl));

    function canal(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    var q2 = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p2 = 2 * l - q2;
    var rr = s === 0 ? l : canal(p2, q2, h + 1 / 3);
    var gg = s === 0 ? l : canal(p2, q2, h);
    var bb = s === 0 ? l : canal(p2, q2, h - 1 / 3);
    var hx = function (v) { return ('0' + Math.round(v * 255).toString(16)).slice(-2); };
    return '#' + hx(rr) + hx(gg) + hx(bb);
  }

  /**
   * Boneca do provador — nossa versão da manequim da Sizebay.
   *
   * A deles é um JPG pré-renderizado por combinação de sliders
   * (`/assets/shapes/v4/new/0/F/toggle-off/B/030303.jpg`): asset do fornecedor,
   * um arquivo por formato, que sai do ar junto com o contrato e não acompanha
   * medida digitada à mão. Esta é desenhada no navegador a partir dos três
   * números, então vale para qualquer corpo — inclusive os que a cliente
   * corrigir na tela "Minhas Medidas".
   *
   * ═══ PROPORÇÃO ═══
   *
   * Escala derivada, não chutada: 420 unidades = 165 cm, logo 2,545 un/cm. Um
   * busto de 88 cm de circunferência dá ~33 cm de largura frontal, ou seja
   * meia-largura ≈ 0,47 × circunferência. Marcos no cânone de 7,5 cabeças:
   * queixo 52, ombro 100, busto 134, cintura 172, quadril 212, virilha 216
   * (= METADE da altura), joelho 316, pé 408.
   *
   * As duas versões anteriores erraram justamente aqui: virilha a 59% da altura
   * (tronco comprido, perna curta) e largura 27% menor que a real. Corpo humano
   * tem largura ≈ 1/4 da altura; fora disso, nenhum sombreado salva.
   *
   * ═══ POR QUE UM PATH SÓ ═══
   *
   * A versão anterior desenhava a metade direita e espelhava com
   * `scale(-1,1)`. Só que o gradiente espelha junto: cada metade ganhava a
   * própria luz e o corpo lia como DOIS cilindros com costura no meio. Aqui o
   * contorno é gerado inteiro — desce pela direita e sobe pela esquerda, com as
   * curvas invertidas — e recebe um gradiente `userSpaceOnUse`, que é o que dá
   * um volume só, iluminado de um lado só.
   *
   * Braços são traços com ponta redonda (`stroke-linecap`), não contornos
   * fechados: um cilindro fino sai perfeito de um traço e vira aberração num
   * path desenhado à mão. O vão entre braço e cintura é proposital — é ele que
   * denuncia um corpo em pé em vez de uma silhueta recortada.
   */
  function boneca(corpo, destaques, recorte) {
    var fator = 0.47;
    var xb = corpo.busto * fator;
    var xc = corpo.cintura * fator;
    var xq = corpo.quadril * fator;
    var xo = Math.max(xb * 1.02, 40);           // ombro
    var C = 100;                                 // eixo do corpo

    var claro = tonalizar(MARFIM, 0.045);
    var meio = MARFIM;
    var escuro = tonalizar(MARFIM, -0.06);
    var fundo = tonalizar(MARFIM, -0.14);

    /* ------------------------------------------------------------- contorno */

    // Cada segmento é uma curva cúbica em OFFSET a partir do eixo: [c1x,c1y,
    // c2x,c2y, px,py]. Guardar assim é o que permite gerar o lado esquerdo
    // invertendo o sinal e a ordem, em vez de espelhar por transform.
    var inicio = [9, 52];
    var segs = [
      [11, 66, 12, 78, 14, 86],                                   // pescoço
      [xo * 0.5, 88, xo * 0.88, 91, xo, 100],                     // trapézio → ombro
      [xo + 1, 108, xb * 0.9, 110, xb * 0.87, 120],               // deltoide → cava
      [xb, 124, xb, 129, xb, 134],                                // busto
      [xb, 148, xc * 1.06, 154, xc, 172],                         // sob-busto → cintura
      [xc * 1.03, 188, xq * 0.95, 194, xq, 212],                  // quadril
      [xq, 230, xq * 0.93, 242, xq * 0.85, 260],                  // coxa
      [xq * 0.7, 286, xq * 0.53, 300, xq * 0.5, 316],             // joelho
      [xq * 0.54, 336, xq * 0.45, 362, xq * 0.28, 388],           // panturrilha
      [xq * 0.26, 399, xq * 0.31, 404, xq * 0.31, 408],           // tornozelo → pé
      [xq * 0.31, 409, xq * 0.09, 409, xq * 0.09, 408],           // sola
      [xq * 0.09, 392, xq * 0.17, 350, xq * 0.15, 316],           // interno da canela
      [xq * 0.14, 286, xq * 0.21, 250, 3, 224],                   // interno da coxa
      [2.4, 220, 0.8, 217, 0, 216]                                // virilha
    ];

    var d = 'M' + (C + inicio[0]) + ',' + inicio[1];
    segs.forEach(function (s) {
      d += 'C' + (C + s[0]) + ',' + s[1] + ' ' + (C + s[2]) + ',' + s[3] + ' ' + (C + s[4]) + ',' + s[5];
    });
    // Volta subindo pela esquerda: mesma sequência ao contrário, x negativo e
    // pontos de controle trocados.
    for (var i = segs.length - 1; i >= 0; i--) {
      var s = segs[i];
      var destino = i === 0 ? inicio : [segs[i - 1][4], segs[i - 1][5]];
      d += 'C' + (C - s[2]) + ',' + s[3] + ' ' + (C - s[0]) + ',' + s[1] +
        ' ' + (C - destino[0]) + ',' + destino[1];
    }
    d += 'Z';

    /* ----------------------------------------------------------- gradientes */

    var largura = Math.max(xq, xo, xb) + 26;
    var g = function (id, x1, x2, paradas) {
      return '<linearGradient id="' + id + '" gradientUnits="userSpaceOnUse" x1="' + x1 +
        '" x2="' + x2 + '">' + paradas + '</linearGradient>';
    };

    var paradasCorpo =
      '<stop offset="0" stop-color="' + escuro + '"/>' +
      '<stop offset="0.14" stop-color="' + meio + '"/>' +
      '<stop offset="0.34" stop-color="' + claro + '"/>' +
      '<stop offset="0.64" stop-color="' + meio + '"/>' +
      '<stop offset="0.9" stop-color="' + escuro + '"/>' +
      '<stop offset="1" stop-color="' + fundo + '"/>';

    var defs =
      g('pvPele', C - largura, C + largura, paradasCorpo) +
      '<radialGradient id="pvSeio" cx="0.38" cy="0.32" r="0.7">' +
      '<stop offset="0" stop-color="' + tonalizar(MARFIM, 0.07) + '"/>' +
      '<stop offset="0.5" stop-color="' + meio + '" stop-opacity="0.35"/>' +
      '<stop offset="1" stop-color="' + fundo + '" stop-opacity="0"/></radialGradient>' +
      '<radialGradient id="pvChao" cx="0.5" cy="0.5" r="0.5">' +
      '<stop offset="0" stop-color="rgba(0,0,0,.16)"/>' +
      '<stop offset="1" stop-color="rgba(0,0,0,0)"/></radialGradient>' +
      '<linearGradient id="pvVinco" gradientUnits="userSpaceOnUse" x1="0" x2="0" y1="216" y2="408">' +
      '<stop offset="0" stop-color="' + fundo + '" stop-opacity="0.55"/>' +
      '<stop offset="0.75" stop-color="' + fundo + '" stop-opacity="0.28"/>' +
      '<stop offset="1" stop-color="' + fundo + '" stop-opacity="0"/></linearGradient>' +
      '<clipPath id="pvClip"><path d="' + d + '"/></clipPath>';

    /* -------------------------------------------------------------- camadas */

    var chao = '<ellipse cx="' + C + '" cy="412" rx="' + (xq * 0.7) + '" ry="5.5" fill="url(#pvChao)"/>';

    // Braços: traço com ponta redonda. Um de cada lado, com coordenadas
    // próprias — nada de espelhar, senão a luz espelha junto.
    function bracoPath(sinal) {
      var o = function (v) { return C + sinal * v; };
      var linha = 'M' + o(xo * 0.8) + ',104 C' + o(xb + 7) + ',150 ' + o(xb + 11) + ',202 ' +
        o(xb + 9) + ',254';
      // Dois traços no mesmo caminho: o de baixo, mais largo e escuro, sobra
      // 1 unidade de cada lado e vira a sombra de contato. Sem ela o braço tem
      // exatamente a cor do tronco e simplesmente desaparece em cima dele.
      return '<path d="' + linha + '" fill="none" stroke="' + fundo + '" stroke-opacity="0.5" ' +
        'stroke-width="21" stroke-linecap="round"/>' +
        '<path d="' + linha + '" fill="none" stroke="url(#pvPele)" stroke-width="18.5" ' +
        'stroke-linecap="round"/>';
    }

    var cabeca =
      '<ellipse cx="' + C + '" cy="30" rx="18" ry="24.5" fill="url(#pvPele)"/>';

    var corpoSvg =
      '<path d="' + d + '" fill="url(#pvPele)"/>' + bracoPath(1) + bracoPath(-1);

    // Volumes e oclusões, recortados pelo contorno para nada vazar.
    var rsx = xb * 0.44, rsy = 20, csx = C + xb * 0.42;
    var sombras =
      '<g clip-path="url(#pvClip)">' +
      '<ellipse cx="' + csx + '" cy="130" rx="' + rsx + '" ry="' + rsy + '" fill="url(#pvSeio)"/>' +
      '<ellipse cx="' + (C * 2 - csx) + '" cy="130" rx="' + rsx + '" ry="' + rsy + '" fill="url(#pvSeio)"/>' +
      // sombra de contato sob cada seio — discreta, senão vira desenho de sutiã
      '<path d="M' + (csx - rsx * 0.75) + ',144 Q' + csx + ',' + (130 + rsy) + ' ' + (csx + rsx * 0.6) + ',140" ' +
      'fill="none" stroke="' + fundo + '" stroke-opacity="0.28" stroke-width="2.2" stroke-linecap="round"/>' +
      '<path d="M' + (C * 2 - csx + rsx * 0.75) + ',144 Q' + (C * 2 - csx) + ',' + (130 + rsy) + ' ' +
      (C * 2 - csx - rsx * 0.6) + ',140" fill="none" stroke="' + fundo + '" stroke-opacity="0.28" ' +
      'stroke-width="2.2" stroke-linecap="round"/>' +
      // sombra sob o queixo, que separa cabeça de tronco
      '<ellipse cx="' + C + '" cy="86" rx="15" ry="7" fill="' + fundo + '" opacity="0.28"/>' +
      // vinco entre as pernas: some perto do tornozelo, onde elas se separam
      '<path d="M' + C + ',218 L' + C + ',406" stroke="url(#pvVinco)" stroke-width="3.6"/>' +
      '</g>';

    /* ----------------------------------------------------------- fita verde */

    var faixas = {
      busto: { y: 132, r: xb },
      cintura: { y: 172, r: xc },
      quadril: { y: 212, r: xq }
    };

    var marcas = (destaques || []).map(function (nome) {
      var f = faixas[nome];
      if (!f) return '';
      var rx = f.r + 3, ry = 8;
      // Volta inteira apagada + frente marcada: a fita passa POR TRÁS do corpo.
      return '<ellipse cx="' + C + '" cy="' + f.y + '" rx="' + rx + '" ry="' + ry + '" fill="none" ' +
        'stroke="#22b07d" stroke-opacity="0.3" stroke-width="3"/>' +
        '<path d="M' + (C - rx) + ',' + f.y + ' A' + rx + ',' + ry + ' 0 0 0 ' + (C + rx) + ',' + f.y + '" ' +
        'fill="none" stroke="#16a34a" stroke-width="3.4" stroke-linecap="round"/>';
    }).join('');

    var meia = Math.max(xb + 17, xq, xo) + 8;

    /**
     * Enquadramento. Corpo inteiro é estreito por natureza, então numa coluna
     * alta ele encolhe. A Sizebay resolve cortando: corpo quase inteiro no
     * ajuste de formato, só o torso no resultado. Mesma ideia aqui.
     */
    var caixa = recorte === 'torso' ? { y: 86, h: 168 } : { y: 2, h: 418 };

    return (
      '<svg class="pv__silhueta" viewBox="' + (C - meia) + ' ' + caixa.y + ' ' + (meia * 2) + ' ' + caixa.h + '" ' +
      'role="img" aria-label="Formato aproximado do corpo">' +
      '<defs>' + defs + '</defs>' +
      chao + cabeca + corpoSvg + sombras + marcas +
      '</svg>'
    );
  }

  /**
   * Reaproveita o canvas que já está no container — recriar a cada ajuste de
   * slider pisca e joga fora o quadro anterior.
   *
   * O canvas tem tamanho FIXO pelo CSS e quem encaixa o corpo dentro dele é o
   * `boneca.js`, que centraliza com a proporção certa. A tentação é declarar
   * `aspect-ratio` e deixar o CSS derivar um lado do outro; não funciona num
   * canvas, porque ele TEM tamanho intrínseco (os atributos width/height) e é o
   * próprio JS que os escreve a partir do tamanho medido — a referência vira
   * circular. No celular isso rendeu um canvas mais alto que a área visível e o
   * manequim aparecia cortado na coxa.
   */
  /**
   * Manequim em SVG — o MESMO corpo do render da VS (`boneca-busto-3.png`),
   * cabeça incluída, traçado por `scripts/prova/tracar-manequim.py` e colado
   * em MQ como PONTOS. Mudou o render? Rode o script e troque MQ.
   *
   * Por que pontos e não caminho pronto: o corpo é DEFORMADO em tempo real.
   * Com os renders, o slider não mexia em nada: cada passo vale 2 cm (±4 no
   * máximo) e o render só troca de variação a cada 7–8 cm. Aqui cada região
   * (busto/cintura/quadril) alarga ou estreita contínua, pesada por uma
   * gaussiana em volta da altura da fita:
   *   - ponto DENTRO do tronco → escala a partir do eixo daquela linha;
   *   - ponto FORA (braço)     → só translada, o braço acompanha sem engordar.
   * `MQ.t` é o tronco por linha [centro, meia-largura], medido no PNG do eixo
   * para fora até o primeiro pixel vazio (pára na axila).
   *
   * Referência 91-75-97 = o render sem deformação (é o corpo da variação 3).
   * `MQ_K` exagera um pouco a física (px do quadro por cm) de propósito: é
   * ilustração, e 2 cm precisam ser VISTOS mexendo.
   */
  var MQ = {"w":408,"h":724,"s":[[197,7],[207,7],[219,10],[227,15],[232,20],[235,26],[238,35],[237,68],[233,85],[230,92],[227,95],[217,99],[216,102],[217,117],[220,121],[231,127],[242,129],[249,132],[257,140],[261,148],[272,197],[282,227],[294,246],[314,289],[334,323],[350,355],[352,366],[354,370],[354,377],[350,390],[347,390],[345,388],[342,394],[338,391],[339,389],[338,386],[335,390],[332,387],[335,374],[334,371],[332,372],[330,382],[328,382],[329,381],[328,364],[322,348],[320,332],[314,323],[278,282],[264,260],[260,249],[258,247],[257,248],[258,265],[268,300],[273,325],[277,357],[277,383],[271,428],[257,475],[255,487],[255,497],[252,508],[252,527],[254,536],[254,569],[249,601],[247,639],[249,652],[252,658],[265,673],[277,682],[285,685],[289,689],[289,694],[287,696],[275,698],[270,696],[260,695],[248,687],[227,685],[222,683],[220,680],[220,670],[224,655],[224,634],[220,610],[216,594],[211,581],[208,565],[208,547],[213,518],[213,509],[210,494],[210,457],[208,442],[203,416],[201,412],[199,414],[188,467],[184,481],[178,494],[175,506],[166,525],[160,569],[146,608],[140,639],[141,673],[145,685],[156,703],[158,708],[158,713],[153,715],[151,714],[138,715],[127,713],[119,708],[116,703],[116,697],[112,689],[112,684],[115,674],[115,662],[118,652],[118,625],[112,574],[112,557],[116,536],[130,508],[136,489],[136,466],[127,393],[127,362],[130,343],[139,314],[149,293],[161,273],[164,265],[165,246],[159,226],[149,205],[148,199],[147,198],[145,200],[139,220],[128,247],[122,271],[113,294],[105,310],[99,330],[99,338],[108,354],[112,372],[107,377],[102,366],[98,368],[96,371],[94,379],[94,388],[99,402],[99,406],[97,406],[95,412],[93,412],[81,397],[75,384],[75,370],[79,354],[83,309],[89,270],[104,230],[108,209],[113,192],[121,149],[127,137],[134,130],[140,127],[146,126],[158,120],[167,114],[174,107],[177,101],[179,88],[175,70],[171,66],[167,57],[168,31],[171,24],[177,16],[187,10]],"s1":[[[156,158],[163,158],[169,164],[173,172],[173,190],[176,194],[183,194],[199,202],[217,203],[220,206],[219,212],[215,217],[193,228],[189,232],[194,244],[202,252],[203,266],[189,276],[187,291],[192,292],[204,287],[225,298],[238,299],[245,304],[250,317],[262,325],[262,331],[259,339],[235,369],[235,372],[241,381],[243,391],[261,401],[262,415],[266,415],[272,391],[273,372],[271,341],[266,326],[266,307],[270,328],[274,360],[273,392],[268,425],[254,472],[252,494],[249,505],[251,566],[245,609],[245,646],[240,638],[236,645],[236,653],[245,669],[259,685],[277,692],[279,692],[280,689],[275,685],[282,688],[285,692],[280,694],[263,692],[251,684],[230,682],[225,680],[223,677],[227,658],[227,631],[223,607],[214,578],[211,562],[211,550],[216,521],[216,506],[213,491],[213,469],[213,454],[206,413],[204,409],[198,409],[196,411],[186,461],[172,503],[163,522],[157,566],[141,613],[137,636],[137,665],[135,648],[133,644],[129,647],[121,666],[121,698],[123,703],[129,709],[121,704],[119,694],[116,689],[118,665],[121,655],[121,622],[115,560],[119,539],[133,511],[139,492],[139,463],[131,401],[130,365],[133,346],[142,317],[167,268],[168,243],[151,196],[144,195],[126,241],[115,279],[105,302],[104,304],[104,301],[119,263],[122,239],[133,219],[140,199],[146,166],[151,160]],[[200,10],[203,12],[199,20],[205,30],[211,52],[216,59],[226,82],[231,74],[235,57],[234,35],[235,58],[228,87],[224,92],[214,96],[213,113],[207,117],[203,117],[195,113],[178,115],[168,123],[142,133],[128,150],[123,171],[121,187],[107,233],[123,156],[130,140],[137,133],[149,129],[170,117],[177,110],[180,104],[182,92],[181,77],[178,67],[171,57],[171,34],[180,19],[190,13]],[[236,195],[240,195],[251,200],[262,201],[268,206],[270,205],[269,197],[277,223],[272,212],[270,210],[269,211],[269,217],[273,224],[277,238],[277,253],[322,317],[324,318],[325,316],[327,319],[334,332],[334,334],[331,330],[330,331],[331,338],[339,350],[342,359],[344,361],[346,359],[345,354],[347,358],[351,373],[349,383],[348,385],[337,383],[338,369],[331,368],[331,361],[324,341],[323,329],[281,279],[267,257],[263,246],[261,244],[256,244],[255,226],[251,221],[243,218],[237,212],[234,205],[234,198]],[[105,237],[91,282],[84,336],[84,350],[80,365],[81,374],[91,384],[91,391],[95,403],[93,404],[82,391],[78,381],[78,373],[82,357],[90,283]],[[152,705],[154,711],[134,710],[150,708]]],"s2":[[[230,342],[238,343],[239,350],[223,368],[218,379],[219,397],[222,408],[223,455],[219,461],[215,461],[213,464],[213,459],[207,417],[205,410],[201,409],[205,388],[204,361],[208,345],[212,343]],[[155,167],[158,167],[161,171],[166,197],[173,208],[177,210],[195,209],[209,211],[208,216],[200,221],[176,222],[172,226],[172,235],[174,241],[174,252],[168,264],[168,243],[165,232],[152,202],[151,196],[146,195],[150,174]],[[245,206],[258,207],[262,210],[263,223],[267,237],[266,252],[277,271],[295,290],[315,317],[317,321],[295,296],[274,269],[261,244],[259,223],[245,213]],[[204,89],[214,89],[219,91],[226,90],[215,95],[213,103],[210,107],[203,109],[196,106],[192,102],[192,94],[196,91]],[[324,335],[327,342],[335,351],[338,362],[343,370],[342,375],[338,375],[338,369],[331,368],[331,361],[325,345]],[[224,679],[239,679],[246,683],[230,682]]],"t":{"100":[212.0,63.0],"102":[212.0,63.0],"104":[212.0,63.0],"106":[212.0,63.0],"108":[212.0,63.0],"110":[212.0,63.0],"112":[212.0,63.0],"114":[212.0,63.0],"116":[212.0,63.0],"118":[212.0,63.0],"120":[212.0,63.0],"122":[212.0,63.0],"124":[212.0,63.0],"126":[212.0,63.0],"128":[212.0,63.0],"130":[212.0,63.0],"132":[212.0,63.0],"134":[212.0,63.0],"136":[212.0,63.0],"138":[212.0,63.0],"140":[212.0,63.0],"142":[212.0,63.0],"144":[212.0,63.0],"146":[212.0,63.0],"148":[212.0,63.0],"150":[212.0,63.0],"152":[212.0,63.0],"154":[212.0,63.0],"156":[212.0,63.0],"158":[212.0,63.0],"160":[212.0,63.0],"162":[212.0,63.0],"164":[212.0,63.0],"166":[212.0,63.0],"168":[212.0,63.0],"170":[212.0,63.0],"172":[212.0,63.0],"174":[212.0,63.0],"176":[212.0,63.0],"178":[212.0,63.0],"180":[212.0,63.0],"182":[212.0,63.0],"184":[212.0,63.0],"186":[212.0,63.0],"188":[212.0,63.0],"190":[212.0,63.0],"192":[212.0,63.0],"194":[212.0,63.0],"196":[212.0,63.0],"198":[212.2,62.9],"200":[212.4,62.8],"202":[212.6,62.7],"204":[213.0,62.7],"206":[213.3,62.6],"208":[213.8,62.5],"210":[214.3,62.4],"212":[214.9,62.3],"214":[215.6,62.1],"216":[216.4,62.0],"218":[217.1,62.0],"220":[217.9,61.9],"222":[218.7,61.9],"224":[219.5,62.0],"226":[220.3,62.0],"228":[221.1,62.0],"230":[222.0,62.2],"232":[222.9,62.3],"234":[223.7,62.5],"236":[224.6,62.8],"238":[223.8,61.4],"240":[222.9,60.0],"242":[221.9,58.5],"244":[220.8,57.0],"246":[219.7,55.6],"248":[218.5,54.1],"250":[217.3,52.6],"252":[215.9,51.1],"254":[214.4,49.6],"256":[212.8,48.2],"258":[211.1,46.7],"260":[211.1,47.0],"262":[211.1,47.3],"264":[211.0,47.7],"266":[211.0,48.1],"268":[210.8,48.6],"270":[210.6,49.2],"272":[210.5,49.8],"274":[210.3,50.5],"276":[210.0,51.2],"278":[209.8,52.0],"280":[209.5,52.8],"282":[209.2,53.5],"284":[208.9,54.4],"286":[208.6,55.1],"288":[208.3,56.0],"290":[208.0,56.8],"292":[207.8,57.6],"294":[207.5,58.4],"296":[207.2,59.2],"298":[207.0,60.0],"300":[206.7,60.7],"302":[206.5,61.5],"304":[206.2,62.2],"306":[206.0,62.9],"308":[205.7,63.5],"310":[205.5,64.2],"312":[205.2,64.9],"314":[205.0,65.5],"316":[204.8,66.0],"318":[204.6,66.6],"320":[204.4,67.1],"322":[204.2,67.7],"324":[204.1,68.2],"326":[204.0,68.7],"328":[203.8,69.1],"330":[203.6,69.6],"332":[203.5,70.0],"334":[203.4,70.5],"336":[203.2,70.9],"338":[203.1,71.2],"340":[203.0,71.6],"342":[202.9,72.0],"344":[202.8,72.3],"346":[202.6,72.6],"348":[202.6,73.0],"350":[202.5,73.3],"352":[202.5,73.5],"354":[202.4,73.8],"356":[202.3,74.0],"358":[202.3,74.3],"360":[202.2,74.5],"362":[202.1,74.6],"364":[202.1,74.7],"366":[202.1,74.8],"368":[202.1,74.9],"370":[202.0,75.0],"372":[202.0,75.0],"374":[202.0,75.0],"376":[201.9,74.9],"378":[201.9,74.9],"380":[201.8,74.8],"382":[201.8,74.8],"384":[201.8,74.7],"386":[201.7,74.5],"388":[201.7,74.4],"390":[201.6,74.3],"392":[201.6,74.1],"394":[201.5,74.0],"396":[201.5,73.9],"398":[201.5,73.8],"400":[201.5,73.8],"402":[201.5,73.6],"404":[201.5,73.5]},"y":{"busto":205,"cintura":284,"quadril":332}};
  var MQ_REF = { busto: 91, cintura: 75, quadril: 97 };
  var MQ_K = 1.8;
  var MQ_SIGMA = { busto: 24, cintura: 22, quadril: 30 };

  var MQ_Y = Object.keys(MQ.t).map(Number).sort(function (a, b) { return a - b; });

  /** Tronco na linha y; fora da faixa medida vale a ponta mais próxima
   *  (pescoço/coxas), para a deformação não dobrar na borda da tabela. */
  function mqTronco(y) {
    var k = Math.max(MQ_Y[0], Math.min(MQ_Y[MQ_Y.length - 1], Math.round(y / 2) * 2));
    return MQ.t[String(k)] || null;
  }

  /** y do quadro → deslocamento horizontal (px) para este corpo. */
  function mqDeslocamento(corpo) {
    var s = {};
    Object.keys(MQ.y).forEach(function (e) {
      var t = mqTronco(MQ.y[e]);
      var hw = t ? t[1] : 60;
      var v = ((corpo && corpo[e]) || MQ_REF[e]) - MQ_REF[e];
      s[e] = Math.max(-0.4 * hw, Math.min(0.4 * hw, v * MQ_K));
    });
    return function (y) {
      var tot = 0;
      Object.keys(s).forEach(function (e) {
        var z = (y - MQ.y[e]) / MQ_SIGMA[e];
        tot += s[e] * Math.exp(-z * z / 2);
      });
      return tot;
    };
  }

  function mqMover(pts, desl) {
    return pts.map(function (p) {
      var t = mqTronco(p[1]);
      var dx = desl(p[1]);
      if (!t || Math.abs(dx) < 0.05) return p;
      var rel = p[0] - t[0];
      if (Math.abs(rel) <= t[1]) return [t[0] + rel * (1 + dx / t[1]), p[1]];
      return [p[0] + (rel < 0 ? -dx : dx), p[1]];
    });
  }

  /** Catmull-Rom fechado → Bézier: curva suave por todos os pontos. */
  function mqCaminho(pts) {
    var n = pts.length;
    var f = function (v) { return Math.round(v * 10) / 10; };
    var d = 'M' + f(pts[0][0]) + ' ' + f(pts[0][1]);
    for (var i = 0; i < n; i++) {
      var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
      d += 'C' + f(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + f(p1[1] + (p2[1] - p0[1]) / 6) + ' ' +
        f(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + f(p2[1] - (p3[1] - p1[1]) / 6) + ' ' + f(p2[0]) + ' ' + f(p2[1]);
    }
    return d + 'Z';
  }

  /**
   * `recorte` 'torso' = da cabeça às coxas (resultado); sem recorte = corpo
   * inteiro (ajuste de formato, Minhas Medidas). viewBox fixo nos dois: o
   * manequim nunca muda de lugar nem de tamanho, só de FORMA.
   */
  var mqSeq = 0;

  /** Cor da fita = cor do selo de caimento daquele eixo. */
  var COR_TOM = { apertado: '#c2483f', justo: '#e0a02c', ideal: '#22b07d', folgado: '#22b07d' };

  function manequim(corpo, destaques, recorte, cores) {
    // ids únicos por desenho: dois SVGs com o mesmo id de clipPath/filtro
    // usam o do PRIMEIRO — o segundo sai recortado pela forma do outro.
    var u = 'pvMq' + (++mqSeq) + '_';
    var desl = mqDeslocamento(corpo);
    var S = mqCaminho(mqMover(MQ.s, desl));
    var torso = recorte === 'torso';
    var H = torso ? 440 : MQ.h, W = MQ.w;
    var vb = torso ? '62 0 284 440' : '40 0 328 ' + MQ.h;

    var sombras = MQ.s1.map(function (p) {
      return '<path d="' + mqCaminho(mqMover(p, desl)) + '" fill="#a89a8c" fill-opacity=".16"/>';
    }).join('') + MQ.s2.map(function (p) {
      return '<path d="' + mqCaminho(mqMover(p, desl)) + '" fill="#958777" fill-opacity=".14"/>';
    }).join('');

    var faixas = (destaques || []).filter(function (e) { return MQ.y[e]; }).map(function (e) {
      var y = MQ.y[e], t = mqTronco(y);
      if (!t) return '';
      var rx = t[1] + desl(y) + 5, ry = rx * 0.18, cx = t[0];
      var cor = (cores && cores[e]) || '#22b07d';
      var gid = 'pvMqG' + e;
      // frente se fundindo na volta pelas pontas; mesma espessura nos dois arcos
      return '<linearGradient id="' + gid + '" gradientUnits="userSpaceOnUse" x1="' + (cx - rx) + '" x2="' + (cx + rx) + '" y1="0" y2="0">' +
        '<stop offset="0" stop-color="' + cor + '" stop-opacity=".3"/><stop offset=".16" stop-color="' + cor + '"/>' +
        '<stop offset=".84" stop-color="' + cor + '"/><stop offset="1" stop-color="' + cor + '" stop-opacity=".3"/></linearGradient>' +
        '<g class="pv__manequim-faixa">' +
        '<path d="M' + (cx - rx) + ' ' + y + ' A' + rx + ' ' + ry + ' 0 0 1 ' + (cx + rx) + ' ' + y + '" stroke="' + cor + '" stroke-opacity=".3"/>' +
        '<path d="M' + (cx - rx) + ' ' + y + ' A' + rx + ' ' + ry + ' 0 0 0 ' + (cx + rx) + ' ' + y + '" stroke="url(#' + gid + ')"/>' +
        '</g>';
    }).join('');

    var fade = torso
      ? '<linearGradient id="pvMqF" x1="0" x2="0" y1="0" y2="1"><stop offset=".84" stop-color="#fff"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>' +
        '<mask id="pvMqM" maskUnits="userSpaceOnUse" x="0" y="-20" width="' + W + '" height="' + (H + 40) + '">' +
        '<rect y="-20" width="' + W + '" height="' + (H + 20) + '" fill="url(#pvMqF)"/></mask>'
      : '';

    return ('<svg class="pv__manequim' + (torso ? ' pv__manequim--torso' : '') + '" viewBox="' + vb + '" role="img" aria-label="Manequim">' +
      '<defs>' +
      '<linearGradient id="pvMqC" x1="0" x2="1"><stop offset="0" stop-color="#ebe5df"/><stop offset=".4" stop-color="#fbfaf8"/>' +
      '<stop offset=".6" stop-color="#f8f6f3"/><stop offset="1" stop-color="#e5ded7"/></linearGradient>' +
      '<linearGradient id="pvMqV" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".5"/>' +
      '<stop offset=".45" stop-color="#fff" stop-opacity="0"/></linearGradient>' +
      '<filter id="pvMqB8" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="8"/></filter>' +
      '<filter id="pvMqB4" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="4"/></filter>' +
      '<filter id="pvMqS" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="#7a6b5e" flood-opacity=".14"/></filter>' +
      '<clipPath id="pvMqK"><path d="' + S + '"/></clipPath>' + fade +
      '</defs>' +
      '<g' + (torso ? ' mask="url(#pvMqM)"' : '') + '>' +
      '<path d="' + S + '" fill="url(#pvMqC)" filter="url(#pvMqS)"/>' +
      '<g clip-path="url(#pvMqK)">' +
      '<g filter="url(#pvMqB8)">' + sombras + '</g>' +
      '<path d="' + S + '" fill="none" stroke="#bfb3a7" stroke-opacity=".75" stroke-width="12" filter="url(#pvMqB4)"/>' +
      '<path d="' + S + '" fill="url(#pvMqV)"/>' +
      '</g>' +
      '<path d="' + S + '" fill="none" stroke="#c7bcb1" stroke-width="1.4"/>' +
      '</g>' +
      '<g fill="none" stroke-width="4.2" stroke-linecap="round">' + faixas + '</g>' +
      '</svg>').replace(/pvMq(?=[A-Z])/g, u);
  }

  function tela(container) {
    var c = container.querySelector('canvas');
    if (!c) {
      container.innerHTML = '';
      c = document.createElement('canvas');
      c.className = 'pv__boneca';
      c.setAttribute('role', 'img');
      c.setAttribute('aria-label', 'Formato aproximado do corpo');
      container.appendChild(c);
    }
    return c;
  }

  /**
   * Pinta a boneca dentro de um container.
   *
   * Ordem de preferência:
   *
   *  1. **Renders da VS** (`boneca.js`) — as cinco variações por região que a
   *     marca entregou, montadas em camadas. É o caminho normal: manequim de
   *     verdade, no mesmo padrão da Sizebay, sem nada aproximado.
   *  2. **Malha 3D** (`boneca3d.js`) — paramétrica, iluminada em tempo real.
   *     Vale quando os PNGs não chegam (CDN fora, asset não publicado).
   *  3. **SVG** — sem WebGL. Perde o volume, mantém a leitura.
   *
   * Os renders são assíncronos: enquanto carregam, o quadro anterior continua
   * na tela (o `clearRect` mora depois do `await`), então trocar de slider não
   * pisca. Se falharem, cai no nível seguinte na hora.
   */
  function pintarBoneca(container, corpo, destaques, recorte, indices) {
    var opcoes = { destaques: destaques, recorte: recorte, indices: indices };

    if (window.VSBoneca && window.VSBoneca.disponivel()) {
      var alvo = tela(container);
      var pintar = function () {
        var velho = fotografar(container, alvo);
        window.VSBoneca.desenhar(alvo, corpo, opcoes).then(function (ok) {
          if (ok !== false) alvo.__pintado = true;
          dissolver(velho);
        }).catch(function () {
          dissolver(velho);
          pintarSemRenders(container, corpo, destaques, recorte);
        });
      };
      // Modal recém-aberto: o layout pode não ter assentado e o canvas ainda
      // medir zero — pintar agora daria um quadro de 1×1.
      if (alvo.clientWidth && alvo.clientHeight) pintar();
      else requestAnimationFrame(pintar);
      return;
    }
    pintarSemRenders(container, corpo, destaques, recorte);
  }

  /**
   * Transição da troca de tamanho: copia o quadro ATUAL para um canvas por
   * cima, pinta o novo embaixo e dissolve a cópia (~200 ms). A cópia também
   * cobre o instante em que o PNG da nova variação ainda está carregando — a
   * cliente nunca vê o canvas limpo. Cliques rápidos: só a cópia mais nova
   * fica; as anteriores saem na hora.
   */
  function fotografar(container, alvo) {
    var antigos = container.querySelectorAll('.pv__boneca-sai');
    if (!alvo.__pintado || alvo.width < 2) {
      antigos.forEach(function (n) { n.remove(); });
      return null;
    }
    var c = document.createElement('canvas');
    c.width = alvo.width;
    c.height = alvo.height;
    c.className = 'pv__boneca-sai';
    c.style.left = alvo.offsetLeft + 'px';
    c.style.top = alvo.offsetTop + 'px';
    c.style.width = alvo.clientWidth + 'px';
    c.style.height = alvo.clientHeight + 'px';
    var x = c.getContext('2d');
    x.drawImage(alvo, 0, 0);
    // Clique/arrasto rápido: a cópia nova guarda o quadro COMO ESTÁ NA TELA —
    // inclusive as cópias ainda sumindo, na opacidade de agora. Remover as
    // antigas sem isso dava um "pulo" (Nicolas, 2026-10-15: transições mais suaves).
    antigos.forEach(function (n) {
      var o = parseFloat(getComputedStyle(n).opacity);
      if (o > 0.01) {
        x.globalAlpha = o;
        x.drawImage(n, 0, 0);
      }
      n.remove();
    });
    x.globalAlpha = 1;
    container.appendChild(c);
    return c;
  }

  function dissolver(velho) {
    if (!velho) return;
    requestAnimationFrame(function () {
      velho.classList.add('is-saindo');
      setTimeout(function () { velho.remove(); }, 460);
    });
  }

  function pintarSemRenders(container, corpo, destaques, recorte) {
    if (!(window.VSBoneca3D && window.VSBoneca3D.suportado())) {
      container.innerHTML = boneca(corpo, destaques, recorte);
      return;
    }

    var alvo = tela(container);
    // O canvas precisa de tamanho antes do primeiro quadro; num modal recém
    // aberto o layout às vezes ainda não assentou.
    var desenhar = function () {
      if (!alvo.clientWidth || !alvo.clientHeight) return false;
      return window.VSBoneca3D.desenhar(alvo, corpo, { destaques: destaques, recorte: recorte });
    };
    if (!desenhar()) requestAnimationFrame(function () { desenhar(); });
  }

  /* ------------------------------------------------------------------- fluxo */

  function Provador(raiz) {
    this.raiz = raiz;
    this.modal = raiz.querySelector('[data-pv-modal]');
    this.caixa = raiz.querySelector('[data-pv-corpo]');
    this.foto = raiz.getAttribute('data-pv-foto') || '';
    this.nome = raiz.getAttribute('data-pv-nome') || '';

    try {
      this.dados = JSON.parse(raiz.getAttribute('data-pv-tabela') || 'null');
    } catch (e) {
      this.dados = null;
    }
    this.imagens = lerJson(raiz.getAttribute('data-pv-imagens'));
    this.variantes = lerJson(raiz.getAttribute('data-pv-variantes'));
    this.modelos = ordenarModelos(raiz.getAttribute('data-pv-modelos'));

    this.peca = 0;
    this.rascunho = null;
    this.bind();
  }

  Provador.prototype.bind = function () {
    var self = this;

    this.raiz.querySelectorAll('[data-pv-abrir]').forEach(function (b) {
      b.addEventListener('click', function () { self.abrir(); });
    });
    this.raiz.querySelectorAll('[data-pv-abrir-tabela]').forEach(function (b) {
      b.addEventListener('click', function () { self.abrir('tabela'); });
    });

    // ESC: o navegador fecharia seco. Cancelamos e saímos pela mesma animação.
    this.modal.addEventListener('cancel', function (e) {
      e.preventDefault();
      self.fechar();
    });

    var fechar = this.raiz.querySelector('[data-pv-fechar]');
    if (fechar) fechar.addEventListener('click', function () { self.fechar(); });

    this.modal.addEventListener('click', function (e) {
      if (e.target === self.modal) self.fechar();
    });

    // ESC fecha o <dialog> por conta do navegador, sem passar por `fechar()`.
    // Sem isto a PDP ficaria travada com o popup já fora da tela.
    this.modal.addEventListener('close', destravarScroll);

    // Quem já tem perfil vê a recomendação na PDP sem abrir nada — é assim que
    // o provador atual se comporta, e é o que faz o recurso trabalhar para quem
    // não clica no botão.
    if (lerPerfil()) {
      this.rascunho = lerPerfil();
      this.publicarNaPagina();
    }
  };

  /** `modo` = 'tabela' abre direto na Tabela de Medidas; sem modo, o provador. */
  Provador.prototype.abrir = function (modo) {
    clearTimeout(this.saindo);
    this.modal.classList.remove('is-saindo');
    this.peca = 0;
    var perfil = lerPerfil();
    this.rascunho = perfil || { altura: null, peso: null, idade: null, ajustes: {} };

    if (modo === 'tabela') this.passoTabela();
    else if (perfil) this.passoResultado();
    else this.passoDados();

    if (!this.modal.open) {
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
    }
    travarScroll();
  };

  /**
   * Sai pela animação e só então fecha o <dialog>: fechar direto corta o
   * popup no meio do quadro. O timeout é a rede para quem tem
   * `prefers-reduced-motion` (sem animação, sem `animationend`).
   */
  Provador.prototype.fechar = function () {
    var self = this;
    if (!this.modal.open || this.modal.classList.contains('is-saindo')) return;
    this.modal.classList.add('is-saindo');
    clearTimeout(this.saindo);
    this.saindo = setTimeout(function () {
      self.modal.classList.remove('is-saindo');
      if (typeof self.modal.close === 'function') self.modal.close();
      else self.modal.removeAttribute('open');
      self.caixa.innerHTML = '';
      destravarScroll();
    }, 180);
  };

  /**
   * Duas colunas: à esquerda a foto ou a silhueta, à direita o painel.
   *
   * Trocar de passo reconstrói tudo, mas a FOTO não pode piscar: se o lado
   * esquerdo novo traz a mesma imagem, o <img> já carregado é reaproveitado e
   * só o painel entra animado. Quando o lado muda de verdade (foto ↔ boneca),
   * ele também entra com fade.
   */
  Provador.prototype.moldura = function (esquerda, classeGrade) {
    var fotoVelha = this.caixa.querySelector('.pv__lado img.pv__foto');
    this.caixa.innerHTML = '';
    // Trocar de passo destrói a gaveta junto com o resto; sem limpar a marca
    // aqui o `×` do modal ficaria escondido para sempre.
    this.modal.classList.remove('pv--com-gaveta');
    var grade = el('div', 'pv__grade' + (classeGrade ? ' ' + classeGrade : ''));
    var lado = el('div', 'pv__lado');
    lado.innerHTML = esquerda;
    var fotoNova = lado.querySelector('img.pv__foto');
    if (fotoVelha && fotoNova && fotoVelha.getAttribute('src') === fotoNova.getAttribute('src')) {
      fotoNova.parentNode.replaceChild(fotoVelha, fotoNova);
    } else {
      lado.classList.add('pv--entrar-suave');
    }
    var painel = el('div', 'pv__painel pv--entrar');
    grade.appendChild(lado);
    grade.appendChild(painel);
    this.caixa.appendChild(grade);
    return painel;
  };

  /**
   * Foto da variante que está escolhida na PDP AGORA, não a do carregamento.
   * O Dawn mantém o `<input name="id">` do formulário em dia a cada troca de
   * cor; o `?variant=` da URL é a segunda fonte. Variante sem foto própria cai
   * na foto com que a página nasceu.
   */
  Provador.prototype.fotoAtual = function () {
    var escopo = this.raiz.closest('.product, [id^="MainProduct-"], section') || document;
    var input = escopo.querySelector('form[action*="/cart/add"] [name="id"]');
    var id = (input && input.value) || new URLSearchParams(location.search).get('variant');
    var img = id && this.variantes[id];
    return (img && this.imagens[img]) || this.foto;
  };

  Provador.prototype.ladoFoto = function () {
    var foto = this.fotoAtual();
    return foto
      ? '<img class="pv__foto" src="' + foto + '" alt="' + this.nome + '">'
      : '<div class="pv__foto pv__foto--vazia"></div>';
  };

  function pontos(atual, total) {
    var s = '';
    for (var i = 0; i < total; i++) {
      s += '<span class="pv__ponto' + (i === atual ? ' is-ativo' : '') + '"></span>';
    }
    return '<div class="pv__pontos">' + s + '</div>';
  }

  /* ---------------------------------------------------------- passo 1: dados */

  Provador.prototype.passoDados = function () {
    var self = this;
    var painel = this.moldura(this.ladoFoto());
    var d = this.rascunho;

    painel.appendChild(el('h2', 'pv__titulo', 'Provador Virtual'));
    painel.appendChild(el('p', 'pv__sub', 'Preencha os dados para experimentar este produto'));

    var campos = el('div', 'pv__campos');

    function campo(rotulo, unidade, valor, dica) {
      var l = el('label', 'pv__campo');
      l.innerHTML =
        '<span class="pv__campo-nome">' + rotulo + '</span>' +
        '<span class="pv__campo-linha">' +
        '<input type="number" inputmode="numeric" class="pv__input"' +
        (valor ? ' value="' + valor + '"' : '') + '>' +
        '<span class="pv__unidade">' + unidade + '</span>' +
        (dica ? '<span class="pv__dica" title="' + dica + '" aria-label="' + dica + '">i</span>' : '') +
        '</span>';
      campos.appendChild(l);
      return l.querySelector('input');
    }

    var altura = campo('Altura', 'cm', d.altura);
    var peso = campo('Peso', 'kg', d.peso);
    // Sem o "i" de dica (removido a pedido do Nicolas, 2026-10-16): era um
    // tooltip de hover, que no celular — 94% do uso — não abria com toque.
    var idade = campo('Idade', 'anos', d.idade);

    painel.appendChild(campos);

    var rodape = el('div', 'pv__rodape');
    rodape.innerHTML = pontos(0, 2);
    var proximo = el('button', 'pv__btn pv__btn--primario', 'Próximo');
    proximo.type = 'button';
    proximo.disabled = true;
    rodape.appendChild(proximo);
    painel.appendChild(rodape);

    var erro = el('p', 'pv__erro');
    erro.hidden = true;
    painel.appendChild(erro);

    function validar() {
      var a = num(altura.value);
      var p = num(peso.value);
      var i = num(idade.value);
      var ok =
        a !== null && a >= 120 && a <= 210 &&
        p !== null && p >= 35 && p <= 180 &&
        i !== null && i >= 14 && i <= 99;
      proximo.disabled = !ok;
      return ok ? { altura: a, peso: p, idade: i } : null;
    }

    [altura, peso, idade].forEach(function (i) { i.addEventListener('input', validar); });
    validar();

    proximo.addEventListener('click', function () {
      var v = validar();
      if (!v) {
        erro.textContent = 'Confira os dados: altura 120–210 cm, peso 35–180 kg, idade 14–99.';
        erro.hidden = false;
        return;
      }
      d.altura = v.altura;
      d.peso = v.peso;
      d.idade = v.idade;
      d.medidas = null;          // dados novos ⇒ estimativa nova
      self.passoForma();
    });
  };

  /**
   * A bolinha do range DESLIZA até o novo passo (~180 ms, ease-out) em vez de
   * pular. O range nativo só aceita valores do `step`, então o step vira "any"
   * durante a animação e volta a 1 no fim, já no valor inteiro.
   */
  function animarRange(range, de, para) {
    if (range.__anim) cancelAnimationFrame(range.__anim);
    clearTimeout(range.__fim);
    var reduzir = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduzir || de === para) { range.step = '1'; range.value = para; return; }
    var t0 = performance.now(), dur = 300;
    function fim() {
      if (range.__anim) cancelAnimationFrame(range.__anim);
      range.__anim = null;
      range.step = '1';
      range.value = para;
    }
    range.step = 'any';
    (function passo() {
      var k = Math.min(1, (performance.now() - t0) / dur);
      var e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2; // ease-in-out, acompanha a dissolução do corpo
      range.value = de + (para - de) * e;
      if (k < 1) range.__anim = requestAnimationFrame(passo);
      else fim();
    })();
    // O rAF pode parar de vir (aba em segundo plano, página ociosa): o valor
    // ficava em 0,99 com step "any". O timeout garante o passo inteiro.
    range.__fim = setTimeout(fim, dur + 60);
  }

  /* ---------------------------------------------------------- passo 2: forma */

  Provador.prototype.passoForma = function () {
    var self = this;
    var d = this.rascunho;
    var base = estimarCorpo(d.altura, d.peso, d.idade);

    var painel = this.moldura('<div data-pv-silhueta></div>');
    var alvo = this.caixa.querySelector('[data-pv-silhueta]');

    function redesenhar(destaque) {
      // Cada passo do slider = UMA variação do render a partir do corpo
      // estimado (as medidas continuam andando PASSO_CM por passo). Sem isso
      // ±4 cm raramente cruzavam a fronteira de 7–8 cm entre variações.
      var indices = null;
      if (window.VSBoneca && window.VSBoneca.indice) {
        indices = {};
        Object.keys(EIXOS).forEach(function (e) {
          indices[e] = Math.max(0, Math.min(6, window.VSBoneca.indice(base[e], e) + (d.ajustes[e] || 0)));
        });
      }
      pintarBoneca(alvo, comAjustes(base, d.ajustes), destaque ? [destaque] : [], undefined, indices);
    }
    redesenhar(null);

    painel.appendChild(el('h2', 'pv__titulo', 'Ajuste o formato do corpo'));
    painel.appendChild(el('p', 'pv__sub',
      'Este é o formato aproximado do corpo que geramos com suas medidas. ' +
      'Ajuste somente se for necessário.'));

    var sliders = el('div', 'pv__sliders');

    Object.keys(EIXOS).forEach(function (eixo) {
      var linha = el('div', 'pv__slider');
      linha.innerHTML =
        '<button type="button" class="pv__passo" data-dir="-1" aria-label="Diminuir ' + EIXOS[eixo] + '">−</button>' +
        '<span class="pv__slider-corpo">' +
        '<span class="pv__slider-nome">' + EIXOS[eixo] + '</span>' +
        '<span class="pv__trilho">' +
        '<input type="range" class="pv__range" min="-' + PASSO_MAX + '" max="' + PASSO_MAX +
        '" step="1" value="' + (d.ajustes[eixo] || 0) + '" aria-label="' + EIXOS[eixo] + '">' +
        '</span>' +
        '</span>' +
        '<button type="button" class="pv__passo" data-dir="1" aria-label="Aumentar ' + EIXOS[eixo] + '">+</button>';

      var range = linha.querySelector('input');

      function aplicar(v, deslizar) {
        var de = parseFloat(range.value);
        d.ajustes[eixo] = Math.max(-PASSO_MAX, Math.min(PASSO_MAX, v));
        if (deslizar) animarRange(range, de, d.ajustes[eixo]);
        else range.value = d.ajustes[eixo];
        redesenhar(eixo);
      }

      range.addEventListener('input', function () { aplicar(parseInt(range.value, 10)); });
      linha.querySelectorAll('.pv__passo').forEach(function (b) {
        b.addEventListener('click', function () {
          aplicar((d.ajustes[eixo] || 0) + parseInt(b.getAttribute('data-dir'), 10), true);
        });
      });

      sliders.appendChild(linha);
    });

    painel.appendChild(sliders);

    var rodape = el('div', 'pv__rodape');
    rodape.innerHTML = pontos(1, 2);
    var voltar = el('button', 'pv__btn', 'Voltar');
    voltar.type = 'button';
    var proximo = el('button', 'pv__btn pv__btn--primario', 'Próximo');
    proximo.type = 'button';
    rodape.appendChild(voltar);
    rodape.appendChild(proximo);
    painel.appendChild(rodape);

    voltar.addEventListener('click', function () { self.passoDados(); });
    proximo.addEventListener('click', function () {
      // Congela a estimativa como MEDIDAS: daqui pra frente a cliente edita
      // centímetro, não "mais um pouco de busto".
      d.medidas = comAjustes(base, d.ajustes);
      salvarPerfil(d);
      self.passoCalculando();
    });
  };

  /* ------------------------------------------------------ passo 3: calculando */

  Provador.prototype.passoCalculando = function () {
    var self = this;
    var painel = this.moldura('<div class="pv__spinner"></div>');
    painel.appendChild(el('h2', 'pv__titulo', 'Quase lá!'));
    painel.appendChild(el('p', 'pv__sub',
      'Estamos calculando as medidas para gerar a recomendação de tamanho ideal'));

    // A conta é instantânea — este intervalo é transição de tela, não cálculo.
    setTimeout(function () {
      if (self.modal.open) self.passoResultado();
    }, 700);
  };

  /* --------------------------------------------------- tela: Minhas Medidas */

  /**
   * Onde a estimativa deixa de mandar. Mesma tela que o provador atual abre em
   * "Editar Medidas": os três números em centímetro, editáveis.
   */
  Provador.prototype.passoMedidas = function () {
    var self = this;
    var d = this.rascunho;
    var corpo = this.corpoFinal();

    var painel = this.moldura('<div data-pv-silhueta></div>');
    var alvo = this.caixa.querySelector('[data-pv-silhueta]');
    var atual = { busto: corpo.busto, cintura: corpo.cintura, quadril: corpo.quadril };

    function redesenhar(destaque) {
      pintarBoneca(alvo, atual, destaque ? [destaque] : []);
    }
    redesenhar(null);

    painel.appendChild(el('h2', 'pv__titulo', 'Minhas Medidas'));
    painel.appendChild(el('p', 'pv__sub', 'Ajuste as medidas conforme necessário'));

    var lista = el('div', 'pv__medidas-lista');
    Object.keys(EIXOS).forEach(function (eixo) {
      var linha = el('label', 'pv__medida');
      linha.innerHTML =
        '<span class="pv__medida-nome">' + EIXOS[eixo] + '</span>' +
        '<span class="pv__campo-linha">' +
        '<input type="number" inputmode="numeric" class="pv__input" value="' + Math.round(atual[eixo]) + '">' +
        '<span class="pv__unidade">cm</span></span>';
      var input = linha.querySelector('input');
      input.addEventListener('input', function () {
        var v = num(input.value);
        if (v !== null && v >= 50 && v <= 160) {
          atual[eixo] = v;
          redesenhar(eixo);
        }
      });
      lista.appendChild(linha);
    });
    painel.appendChild(lista);

    var rodape = el('div', 'pv__rodape pv__rodape--fim');
    var reiniciar = el('button', 'pv__btn', 'Reiniciar');
    reiniciar.type = 'button';
    var ver = el('button', 'pv__btn pv__btn--primario', 'Ver Recomendação');
    ver.type = 'button';
    rodape.appendChild(reiniciar);
    rodape.appendChild(ver);
    painel.appendChild(rodape);

    reiniciar.addEventListener('click', function () {
      limparPerfil();
      self.rascunho = { altura: null, peso: null, idade: null, ajustes: {} };
      self.limparDaPagina();
      self.passoDados();
    });

    ver.addEventListener('click', function () {
      d.medidas = atual;
      salvarPerfil(d);
      self.passoResultado();
    });
  };

  /* ------------------------------------------------------ passo 4: resultado */

  Provador.prototype.corpoFinal = function () {
    var d = this.rascunho;
    if (d.medidas && d.medidas.busto) return d.medidas;
    return comAjustes(estimarCorpo(d.altura, d.peso, d.idade), d.ajustes);
  };

  Provador.prototype.passoResultado = function () {
    var self = this;
    var corpo = this.corpoFinal();
    var pecas = this.dados.pecas;
    var peca = pecas[this.peca] || pecas[0];
    var av = avaliar(peca, corpo);

    var painel = this.moldura(
      this.ladoFoto() +
      '<button type="button" class="pv__aba-medidas" data-pv-medidas-corporais>Medidas<br>corporais</button>' +
      '<div class="pv__gaveta" data-pv-gaveta></div>'
    );

    var gaveta = this.montarGaveta(peca);

    if (pecas.length > 1) {
      painel.appendChild(this.abasPecas(function () { self.passoResultado(); }));
    }

    var topo = el('div', 'pv__resultado');

    var coluna = el('div', 'pv__coluna');
    coluna.appendChild(el('p', 'pv__label', 'Melhor opção'));
    var cartao = el('div', 'pv__cartao');
    cartao.innerHTML =
      '<span class="pv__tamanho">' + av.melhor.nome + '</span>' +
      '<span class="pv__check" aria-hidden="true">✓</span>';
    coluna.appendChild(cartao);
    var editar = el('button', 'pv__btn pv__btn--pequeno', 'Editar Medidas');
    editar.type = 'button';
    editar.addEventListener('click', function () { self.passoMedidas(); });
    coluna.appendChild(editar);
    topo.appendChild(coluna);

    var bonecaCaixa = el('div', 'pv__resultado-silhueta');
    topo.appendChild(bonecaCaixa);

    var caimento = el('div', 'pv__caimento');
    topo.appendChild(caimento);

    painel.appendChild(topo);

    painel.appendChild(el('p', 'pv__prove', 'Compare com outros tamanhos:'));

    var chips = el('div', 'pv__chips');
    av.lista.forEach(function (t) {
      var b = el('button', 'pv__chip', '<span>' + t.nome + '</span>');
      b.type = 'button';
      b.setAttribute('data-tam', t.nome);
      b.addEventListener('click', function () { mostrar(t.nome); });
      chips.appendChild(b);
    });
    painel.appendChild(chips);

    var rodape = el('div', 'pv__rodape pv__rodape--fim');
    var fechar = el('button', 'pv__btn pv__btn--primario', 'Fechar');
    fechar.type = 'button';
    fechar.addEventListener('click', function () { self.fechar(); });
    rodape.appendChild(fechar);
    painel.appendChild(rodape);

    /** Troca o tamanho em exibição: caimento, faixas na silhueta e chip marcado. */
    function mostrar(nome) {
      var t = av.lista.filter(function (x) { return x.nome === nome; })[0];
      if (!t) return;
      var trocou = cartao.getAttribute('data-tam') !== t.nome;
      cartao.setAttribute('data-tam', t.nome);

      cartao.querySelector('.pv__tamanho').textContent = t.nome;
      cartao.classList.toggle('is-recomendado', t.nome === av.melhor.nome);
      if (trocou) {
        animar(cartao.querySelector('.pv__tamanho'), 'pv--trocar');
        animar(caimento, 'pv--trocar');
      }
      gaveta.mostrarTamanho(t.medidas);

      caimento.innerHTML = t.eixos.map(function (x) {
        return '<span class="pv__pill pv__pill--' + x.rotulo.tom + '">' +
          '<i class="pv__pill-icone">' + ICONES[x.rotulo.icone] + '</i>' +
          '<b>' + x.rotulo.texto + '</b>' +
          '<em>' + EIXOS[x.eixo] + '</em></span>';
      }).join('');

      var cores = {};
      t.eixos.forEach(function (x) { cores[x.eixo] = COR_TOM[x.rotulo.tom] || COR_TOM.ideal; });
      bonecaCaixa.innerHTML = manequim(null, t.eixos.map(function (x) { return x.eixo; }), 'torso', cores);

      chips.querySelectorAll('.pv__chip').forEach(function (b) {
        b.classList.toggle('is-ativo', b.getAttribute('data-tam') === nome);
        b.classList.toggle('is-recomendado', b.getAttribute('data-tam') === av.melhor.nome);
      });
    }

    mostrar(av.melhor.nome);
    this.publicarNaPagina();
  };

  /**
   * Abas das peças (Top / Calcinha) — as mesmas no resultado e na tabela.
   * `aoTrocar` redesenha a tela; a foto não pisca porque a moldura reaproveita.
   */
  Provador.prototype.abasPecas = function (aoTrocar) {
    var self = this;
    var abas = el('div', 'pv__abas');
    this.dados.pecas.forEach(function (p, i) {
      var b = el('button', 'pv__aba' + (i === self.peca ? ' is-ativa' : ''), p.rotulo);
      b.type = 'button';
      b.addEventListener('click', function () {
        if (i === self.peca) return;
        self.peca = i;
        aoTrocar();
      });
      abas.appendChild(b);
    });
    return abas;
  };

  /**
   * Gaveta "Medidas corporais", no desenho da Sizebay: cartão branco que entra
   * da esquerda por cima da foto, centrado na altura, com a aba preta `‹`
   * pendurada na borda direita. Mostra as medidas de corpo que o tamanho EM
   * EXIBIÇÃO veste — trocar de tamanho nos chips troca os números aqui.
   *
   * A tabela inteira saiu daqui: o botão abre a tela "Tabela de tamanhos".
   * Devolve o nó com `mostrarTamanho(medidas)` para o resultado chamar.
   */
  Provador.prototype.montarGaveta = function (peca) {
    var self = this;
    var gaveta = this.caixa.querySelector('[data-pv-gaveta]');
    var botao = this.caixa.querySelector('[data-pv-medidas-corporais]');
    if (!gaveta || !botao) return { mostrarTamanho: function () {} };

    gaveta.hidden = false;
    gaveta.setAttribute('aria-hidden', 'true');
    gaveta.innerHTML =
      '<div class="pv__gaveta-conteudo">' +
      '<h3 class="pv__gaveta-titulo">Medidas corporais</h3>' +
      '<div class="pv__gaveta-linhas" data-pv-gaveta-linhas></div>' +
      '<button type="button" class="pv__btn pv__btn--cinza" data-pv-ver-tabela>Tabela de Medidas</button>' +
      (this.modelos.length ? '<button type="button" class="pv__btn pv__btn--cinza" data-pv-ver-modelos>Medidas das modelos</button>' : '') +
      '</div>' +
      '<button type="button" class="pv__gaveta-fechar" data-pv-gaveta-fechar aria-label="Fechar medidas corporais">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 6.5 9 12l5.5 5.5"/></svg>' +
      '</button>';

    var caixaLinhas = gaveta.querySelector('[data-pv-gaveta-linhas]');
    var tamanhoAtual = null;

    gaveta.mostrarTamanho = function (medidas) {
      tamanhoAtual = medidas.nome;
      caixaLinhas.innerHTML = peca.eixos
        .filter(function (e) { return typeof medidas[e] === 'number'; })
        .map(function (e) {
          return '<div class="pv__gaveta-linha"><span>' + (e === 'busto' ? 'Tórax / Busto' : EIXOS[e]) +
            '</span><b>' + medidas[e] + 'cm</b></div>';
        }).join('');
      animar(caixaLinhas, 'pv--trocar');
    };

    function mostrar(aberta) {
      gaveta.classList.toggle('is-aberta', aberta);
      gaveta.setAttribute('aria-hidden', aberta ? 'false' : 'true');
      botao.classList.toggle('is-escondida', aberta);
      self.modal.classList.toggle('pv--com-gaveta', aberta);
    }

    botao.addEventListener('click', function () { mostrar(true); });
    gaveta.querySelector('[data-pv-gaveta-fechar]').addEventListener('click', function () { mostrar(false); });
    gaveta.querySelector('[data-pv-ver-tabela]').addEventListener('click', function () {
      self.passoTabela(tamanhoAtual);
    });
    var verModelos = gaveta.querySelector('[data-pv-ver-modelos]');
    if (verModelos) verModelos.addEventListener('click', function () { self.passoModelos(tamanhoAtual); });
    return gaveta;
  };

  /* ------------------------------------------------- tela: Tabela de tamanhos */

  /**
   * "Tabela de tamanhos", como a Sizebay abre pelo botão "Tabela de Medidas":
   * à esquerda a foto da variante e o link "Fita métrica" (como medir); à
   * direita os tamanhos num carrossel com setas, as medidas do tamanho
   * escolhido e o botão preto que leva ao provador.
   *
   * Abre direto da PDP (sem medida nenhuma) ou da gaveta do resultado. Com
   * perfil salvo, já nasce no tamanho recomendado.
   */
  Provador.prototype.passoTabela = function (tamanhoInicial) {
    var self = this;
    var pecas = this.dados.pecas;
    var peca = pecas[this.peca] || pecas[0];

    if (!tamanhoInicial && lerPerfil()) {
      tamanhoInicial = avaliar(peca, this.corpoFinal()).melhor.nome;
    }
    var i = Math.max(0, peca.tamanhos.map(function (t) { return t.nome; }).indexOf(tamanhoInicial));

    var painel = this.moldura(
      '<div class="pv__tabela-lado">' +
      '<h2 class="pv__tabela-titulo">Tabela de tamanhos</h2>' +
      '<div class="pv__tabela-foto">' + this.ladoFoto() + guiaMedidas() + '</div>' +
      '<div class="pv__links">' +
      '<button type="button" class="pv__link" data-pv-fita aria-expanded="false">Fita métrica</button>' +
      (this.modelos.length ? '<button type="button" class="pv__link" data-pv-ver-modelos>Medidas das modelos</button>' : '') +
      '</div>' +
      '</div>',
      'pv__grade--tabela'
    );

    var verModelos = this.caixa.querySelector('[data-pv-ver-modelos]');
    if (verModelos) {
      verModelos.addEventListener('click', function () {
        self.passoModelos(peca.tamanhos[i] && peca.tamanhos[i].nome);
      });
    }

    var fita = this.caixa.querySelector('[data-pv-fita]');
    var moldFoto = this.caixa.querySelector('.pv__tabela-foto');
    fita.addEventListener('click', function () {
      var aberto = moldFoto.classList.toggle('is-guia');
      fita.setAttribute('aria-expanded', aberto ? 'true' : 'false');
      fita.textContent = aberto ? 'Ver foto' : 'Fita métrica';
    });

    if (pecas.length > 1) {
      painel.appendChild(this.abasPecas(function () { self.passoTabela(); }));
    }

    painel.appendChild(el('p', 'pv__tabela-instrucao', 'Selecione um tamanho e verifique as medidas'));

    var carrossel = el('div', 'pv__carrossel');
    var ant = el('button', 'pv__seta', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M14.5 6.5 9 12l5.5 5.5"/></svg>');
    ant.type = 'button';
    ant.setAttribute('aria-label', 'Tamanho anterior');
    var prox = el('button', 'pv__seta', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M9.5 6.5 15 12l-5.5 5.5"/></svg>');
    prox.type = 'button';
    prox.setAttribute('aria-label', 'Próximo tamanho');
    var trilho = el('div', 'pv__trilho-tam');
    var recomendado = lerPerfil() ? avaliar(peca, this.corpoFinal()).melhor.nome : null;

    peca.tamanhos.forEach(function (t, k) {
      var b = el('button', 'pv__tam' + (t.nome === recomendado ? ' is-recomendado' : ''), t.nome);
      b.type = 'button';
      b.addEventListener('click', function () { escolher(k); });
      trilho.appendChild(b);
    });
    carrossel.appendChild(ant);
    carrossel.appendChild(trilho);
    carrossel.appendChild(prox);
    painel.appendChild(carrossel);

    var linhas = el('div', 'pv__tabela-linhas');
    painel.appendChild(linhas);

    // "Medidas do produto" = as medidas da PEÇA (não do corpo), por tamanho.
    // Some quando o produto não tem nenhuma para o tamanho escolhido.
    var medidasPeca = el('div', 'pv__produto-medidas');
    painel.appendChild(medidasPeca);

    var rodape = el('div', 'pv__rodape pv__rodape--cheio');
    var irProvador = el('button', 'pv__btn pv__btn--primario pv__btn--grande', 'Provador Virtual');
    irProvador.type = 'button';
    irProvador.addEventListener('click', function () {
      if (lerPerfil()) self.passoResultado();
      else self.passoDados();
    });
    rodape.appendChild(irProvador);
    painel.appendChild(rodape);

    ant.addEventListener('click', function () { escolher(i - 1); });
    prox.addEventListener('click', function () { escolher(i + 1); });

    function escolher(k, primeira) {
      k = Math.max(0, Math.min(peca.tamanhos.length - 1, k));
      var trocou = k !== i;
      i = k;
      var t = peca.tamanhos[i];
      trilho.querySelectorAll('.pv__tam').forEach(function (b, n) {
        b.classList.toggle('is-ativo', n === i);
        b.setAttribute('aria-pressed', n === i ? 'true' : 'false');
      });
      ant.disabled = i === 0;
      prox.disabled = i === peca.tamanhos.length - 1;

      var alvo = trilho.children[i];
      if (alvo) {
        trilho.scrollTo({
          left: alvo.offsetLeft - (trilho.clientWidth - alvo.offsetWidth) / 2,
          behavior: primeira ? 'auto' : 'smooth'
        });
      }

      linhas.innerHTML = Object.keys(t)
        .filter(function (e) { return e !== 'nome' && typeof t[e] === 'number'; })
        .map(function (e) {
          return '<div class="pv__tabela-linha"><span>' + (ROTULOS[e] || e) + '</span><span>' + t[e] + ' cm</span></div>';
        }).join('');
      if (trocou || primeira) animar(linhas, 'pv--trocar');

      medidasPeca.innerHTML = medidasDoProduto(self.dados.medidasProduto, t.nome);
      if (trocou || primeira) animar(medidasPeca, 'pv--trocar');
    }

    escolher(i, true);
  };

  function semPonto(t) {
    return String(t == null ? '' : t).replace(/\./g, '').trim().toUpperCase();
  }

  function escapar(t) {
    return String(t).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** Só número (ou faixa "60-64") vira "N cm"; texto livre vai como a Laís escreveu. */
  function comCm(v) {
    v = String(v).trim();
    return /^\d+([.,]\d+)?(\s*[-–a]\s*\d+([.,]\d+)?)?$/.test(v) ? escapar(v) + ' cm' : escapar(v);
  }

  /**
   * Card "Medidas do produto" de um tamanho, vindo de `$app:medidas-produto`
   * ({colunas, descricoes, tamanhos:[{tamanho, valores}]}). Sem dado → ''.
   * Abaixo das linhas, "Medida: como medir" para cada coluna que tem descrição.
   */
  function medidasDoProduto(g, tamanho) {
    if (!g || !g.colunas || !g.tamanhos) return '';
    var linha = g.tamanhos.filter(function (t) { return semPonto(t.tamanho) === semPonto(tamanho); })[0];
    if (!linha) return '';
    var itens = g.colunas.map(function (c, k) {
      var v = linha.valores && linha.valores[k];
      return c && v != null && String(v).trim() !== ''
        ? '<div class="pv__tabela-linha"><span>' + escapar(c) + '</span><span>' + comCm(v) + '</span></div>'
        : '';
    }).join('');
    if (!itens) return '';
    var descricoes = g.colunas.map(function (c, k) {
      var d = g.descricoes && g.descricoes[k];
      return c && d && String(d).trim()
        ? '<p><b>' + escapar(c) + ':</b> ' + escapar(String(d).trim()) + '</p>'
        : '';
    }).join('');
    return '<h3 class="pv__produto-titulo">Medidas do produto</h3>' + itens +
      (descricoes ? '<div class="pv__produto-como">' + descricoes + '</div>' : '');
  }

  /**
   * Modelos na ordem da Laís (`ordem`); sem ordem, as que aparecem nas fotos
   * deste produto vêm primeiro, depois por nome.
   */
  function ordenarModelos(txt) {
    var lista;
    try { lista = JSON.parse(txt || '[]'); } catch (e) { lista = []; }
    if (!Array.isArray(lista)) return [];
    return lista.filter(function (m) { return m && m.nome; }).sort(function (a, b) {
      var oa = num(a.ordem), ob = num(b.ordem);
      if (oa != null || ob != null) return (oa == null ? 1e9 : oa) - (ob == null ? 1e9 : ob);
      if (!!a.nesta !== !!b.nesta) return a.nesta ? -1 : 1;
      return String(a.nome).localeCompare(String(b.nome), 'pt-BR');
    });
  }

  /** 158 → "1,58 m". */
  function metros(cm) {
    var n = num(cm);
    return n == null ? '' : (n / 100).toFixed(2).replace('.', ',') + ' m';
  }

  /** Só as medidas preenchidas de uma modelo — campo vazio não aparece. */
  function linhasModelo(m) {
    var campos = [
      ['Altura', m.altura != null ? metros(m.altura) : ''],
      ['Peso', m.peso != null ? String(m.peso).replace('.', ',') + ' kg' : ''],
      ['Busto', m.busto != null ? m.busto + ' cm' : ''],
      ['Sob o busto', m.sobBusto != null ? m.sobBusto + ' cm' : ''],
      ['Cintura', m.cintura != null ? m.cintura + ' cm' : ''],
      ['Quadril', m.quadril != null ? m.quadril + ' cm' : '']
    ];
    return campos.filter(function (c) { return c[1]; }).map(function (c) {
      return '<span class="pv__modelo-linha"><b>' + escapar(c[1]) + '</b><span>' + c[0] + '</span></span>';
    }).join('');
  }

  /** "Veste P" ou "Veste top P · calcinha M" — só o que estiver preenchido. */
  function vesteModelo(m) {
    var partes = [];
    if (m.vesteTop) partes.push('top ' + m.vesteTop);
    if (m.vesteCalcinha) partes.push('calcinha ' + m.vesteCalcinha);
    if (partes.length) return 'Veste ' + partes.join(' · ');
    return m.tamanho ? 'Veste ' + m.tamanho : '';
  }

  /* ------------------------------------------------ tela: Medidas das modelos */

  /**
   * "Medidas das modelos": a mesma lista em todo produto (metaobject `modelo`).
   * À esquerda a foto da modelo escolhida (capa → foto → a do produto); à
   * direita um card por modelo com SÓ as medidas preenchidas. "Tabela de
   * tamanhos" volta no mesmo tamanho em que estava.
   */
  Provador.prototype.passoModelos = function (tamanhoVolta) {
    var self = this;
    var modelos = this.modelos;
    var fotoProduto = this.ladoFoto();

    var painel = this.moldura(
      '<div class="pv__tabela-lado">' +
      '<h2 class="pv__tabela-titulo">Medidas das modelos</h2>' +
      '<div class="pv__tabela-foto" data-pv-modelo-foto>' + fotoProduto + '</div>' +
      '<div class="pv__links"><button type="button" class="pv__link" data-pv-voltar-tabela>Tabela de tamanhos</button></div>' +
      '</div>',
      'pv__grade--tabela pv__grade--modelos'
    );

    this.caixa.querySelector('[data-pv-voltar-tabela]').addEventListener('click', function () {
      self.passoTabela(tamanhoVolta);
    });

    painel.appendChild(el('p', 'pv__tabela-instrucao',
      modelos.length > 1 ? 'Escolha uma modelo para ver a foto' : 'As medidas da nossa modelo'));

    var lista = el('div', 'pv__modelos');
    modelos.forEach(function (m, k) {
      var card = el('button', 'pv__modelo');
      card.type = 'button';
      var veste = vesteModelo(m);
      card.innerHTML =
        (m.foto || m.capa
          ? '<img class="pv__modelo-foto" src="' + escapar(m.foto || m.capa) + '" alt="" loading="lazy">'
          : '<span class="pv__modelo-foto pv__modelo-foto--vazia" aria-hidden="true">' + escapar(String(m.nome).charAt(0)) + '</span>') +
        '<span class="pv__modelo-info">' +
        '<span class="pv__modelo-topo">' +
        '<span class="pv__modelo-nome">' + escapar(m.nome) + '</span>' +
        (veste ? '<span class="pv__modelo-veste">' + escapar(veste) + '</span>' : '') +
        '</span>' +
        (m.nesta ? '<span class="pv__modelo-selo">Nas fotos deste produto</span>' : '') +
        '<span class="pv__modelo-linhas">' + linhasModelo(m) + '</span>' +
        '</span>';
      card.addEventListener('click', function () { escolher(k); });
      lista.appendChild(card);
    });
    painel.appendChild(lista);

    var rodape = el('div', 'pv__rodape pv__rodape--cheio');
    var irProvador = el('button', 'pv__btn pv__btn--primario pv__btn--grande', 'Provador Virtual');
    irProvador.type = 'button';
    irProvador.addEventListener('click', function () {
      if (lerPerfil()) self.passoResultado();
      else self.passoDados();
    });
    rodape.appendChild(irProvador);
    painel.appendChild(rodape);

    var moldFoto = this.caixa.querySelector('[data-pv-modelo-foto]');

    function escolher(k, primeira) {
      lista.querySelectorAll('.pv__modelo').forEach(function (c, n) {
        c.classList.toggle('is-ativo', n === k);
        c.setAttribute('aria-pressed', n === k ? 'true' : 'false');
      });
      var m = modelos[k];
      var src = m && (m.capa || m.foto);
      var html = src
        ? '<img class="pv__foto" src="' + escapar(src) + '" alt="' + escapar(m.nome) + '">'
        : fotoProduto;
      if (primeira) { moldFoto.innerHTML = html; return; }
      // Crossfade: a nova entra POR CIMA, transparente; carregou, aparece e a antiga sai.
      var velhas = [].slice.call(moldFoto.querySelectorAll('.pv__foto'));
      var nova = el('div', null, html).firstChild;
      if (!nova) return;
      nova.classList.add('is-chegando');
      moldFoto.appendChild(nova);
      var mostrar = function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { nova.classList.remove('is-chegando'); });
        });
        setTimeout(function () {
          velhas.forEach(function (v) { if (v.parentNode) v.parentNode.removeChild(v); });
        }, 700);
      };
      if (nova.tagName === 'IMG' && !nova.complete) {
        nova.addEventListener('load', mostrar, { once: true });
        nova.addEventListener('error', mostrar, { once: true });
      } else {
        mostrar();
      }
    }

    if (modelos.length) escolher(0, true);
  };

  /**
   * "Fita métrica": como tirar cada medida. Mora por cima da foto e entra com
   * fade — é ajuda, não passo, então não troca de tela.
   */
  function guiaMedidas() {
    var itens = [
      ['Busto', 'Passe a fita na parte mais cheia do busto, sem apertar, paralela ao chão.'],
      ['Sob o busto', 'Logo abaixo do busto, onde fica o elástico do sutiã.'],
      ['Cintura', 'Na parte mais fina do tronco, normalmente um pouco acima do umbigo.'],
      ['Quadril', 'Na parte mais larga do quadril e do bumbum, com os pés juntos.']
    ];
    return '<div class="pv__guia" aria-live="polite">' +
      '<h3 class="pv__guia-titulo">Como medir</h3>' +
      '<p class="pv__guia-sub">Use uma fita métrica flexível, sobre a pele ou roupa fina.</p>' +
      '<ol class="pv__guia-lista">' + itens.map(function (x) {
        return '<li><b>' + x[0] + '</b><span>' + x[1] + '</span></li>';
      }).join('') + '</ol></div>';
  }

  /* ------------------------------------------- recomendação na própria PDP */

  /**
   * Escreve "Recomendamos o tamanho X" embaixo do seletor de tamanho, como o
   * provador atual faz. É o que faz o recurso valer para quem não abre o modal:
   * a resposta aparece onde a decisão acontece.
   *
   * Casa a peça com o fieldset pelo texto do legend (no conjunto, "Top Biki
   * Fita (Tamanho)" × peça "Top"). Sem casar, não escreve nada — errar de peça
   * é pior do que ficar quieto.
   */
  Provador.prototype.publicarNaPagina = function () {
    var self = this;
    if (!this.rascunho || !this.dados) return;
    var corpo = this.corpoFinal();
    if (!corpo || !corpo.busto) return;

    var escopo = this.raiz.closest('.product, [id^="MainProduct-"], section') || document;
    var fieldsets = [].slice.call(escopo.querySelectorAll('fieldset.product-form__input--pill'));
    if (!fieldsets.length) return;

    this.dados.pecas.forEach(function (peca, i) {
      var av = avaliar(peca, corpo);
      var alvo = null;

      if (self.dados.pecas.length === 1) {
        // O seletor de TAMANHO, não o primeiro: em Macacão Urban & cia. a cor vem antes.
        alvo = fieldsets.filter(function (f) {
          var l = f.querySelector('legend');
          return l && /tamanho|size/i.test(l.textContent);
        })[0] || fieldsets[0];
      } else {
        var chave = (peca.rotulo || '').toLowerCase();
        alvo = fieldsets.filter(function (f) {
          var l = f.querySelector('legend');
          return l && l.textContent.toLowerCase().indexOf(chave) > -1;
        })[0] || fieldsets[i] || null;
      }
      if (!alvo) return;

      var linha = alvo.parentNode.querySelector('[data-pv-recomendacao="' + peca.id + '"]');
      if (!linha) {
        linha = el('p', 'pv__recomendacao');
        linha.setAttribute('data-pv-recomendacao', peca.id);
        alvo.parentNode.insertBefore(linha, alvo.nextSibling);
      }
      linha.innerHTML = 'Recomendamos o tamanho <b>' + av.melhor.nome + '</b>';
    });
  };

  Provador.prototype.limparDaPagina = function () {
    var escopo = this.raiz.closest('.product, [id^="MainProduct-"], section') || document;
    escopo.querySelectorAll('[data-pv-recomendacao]').forEach(function (n) { n.remove(); });
  };

  /* -------------------------------------------------------------------- init */

  function init() {
    document.querySelectorAll('[data-pv]').forEach(function (raiz) {
      if (raiz.__provador) return;
      var p = new Provador(raiz);
      if (!p.dados || !p.dados.pecas || !p.dados.pecas.length) return; // sem tabela, sem provador
      raiz.__provador = p;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
})();
